/**
 * WeatherAlertsTab
 * 
 * This component manages and displays weather-related alerts and warnings for power grid towers.
 * It categorizes alerts based on severity and provides a mechanism for operators to acknowledge
 * and track weather-related risks.
 * 
 * Alert Categories:
 * 1. Current Alerts (Real-time conditions):
 *    - High Wind Alerts (>50 km/h): Risk of structural stress
 *    - Lightning Alerts: Risk of electrical damage
 *    - Heavy Precipitation (>5 mm): Risk of flooding/ground instability
 *    - Temperature Extremes: Risk of thermal expansion/contraction
 * 
 * 2. Forecast Alerts (Upcoming conditions):
 *    - Predicted severe weather in next 12 hours
 *    - Early warning system for maintenance planning
 *    - Allows proactive risk management
 * 
 * Risk Levels and Thresholds:
 * 1. High Risk (Red):
 *    - Wind speed > 50 km/h: Risk of tower oscillation
 *    - Lightning detected: Risk of electrical damage
 *    - Heavy precipitation > 5 mm: Risk of flooding
 * 
 * 2. Medium Risk (Orange):
 *    - Wind speed > 30 km/h: Increased monitoring needed
 *    - Precipitation > 2 mm: Ground condition monitoring
 *    - Temperature > 35°C or < 0°C: Thermal stress monitoring
 * 
 * Alert Acknowledgment System:
 * - Purpose: Track operator awareness of alerts
 * - When an alert is acknowledged:
 *   1. It's marked as "seen" in the system
 *   2. Creates an audit trail of alert handling
 *   3. Helps in compliance and safety documentation
 * - Separate acknowledgment for current vs forecast alerts
 * - Does not dismiss the alert, only marks it as seen
 */

"use client"

import React from "react"
import { View, StyleSheet, ScrollView } from "react-native"
import { Text, useTheme, Card, Button, List, Divider } from "react-native-paper"
import { AlertTriangle, Wind, Thermometer, CloudRain, Zap } from 'lucide-react'

interface WeatherAlertsTabProps {
  weatherData: any
  forecastData: any[]
}

export const WeatherAlertsTab: React.FC<WeatherAlertsTabProps> = ({ weatherData, forecastData }) => {
  const theme = useTheme()

  // Handle missing data
  if (!weatherData || !forecastData || forecastData.length === 0) {
    return (
      <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
        <Text>No weather data available for alerts.</Text>
      </View>
    )
  }

  // Process current weather alerts based on thresholds
  const currentAlerts = []
  const { values } = weatherData.data

  // High Wind Alerts
  if (values.windSpeed > 50) {
    currentAlerts.push({
      type: "High Wind",
      severity: "High",
      message: `Wind speed of ${values.windSpeed} km/h exceeds safety limits.`,
      icon: Wind,
      color: "#f5222d",
      details: "Immediate action required. Consider emergency protocols for high wind conditions."
    })
  } else if (values.windSpeed > 30) {
    currentAlerts.push({
      type: "Moderate Wind",
      severity: "Medium",
      message: `Wind speed of ${values.windSpeed} km/h may cause oscillation.`,
      icon: Wind,
      color: "#faad14",
      details: "Increased monitoring recommended. Check structural integrity if persistent."
    })
  }

  // Lightning Alerts - Any detection is high risk
  if (values.lightningStrikeCount > 0) {
    currentAlerts.push({
      type: "Lightning",
      severity: "High",
      message: `${values.lightningStrikeCount} lightning strikes detected nearby.`,
      icon: Zap,
      color: "#f5222d",
      details: "Immediate risk to equipment. Activate lightning protection protocols."
    })
  }

  // Precipitation Alerts
  if (values.precipitationIntensity > 5) {
    currentAlerts.push({
      type: "Heavy Precipitation",
      severity: "High",
      message: `Heavy precipitation of ${values.precipitationIntensity} mm may cause flooding.`,
      icon: CloudRain,
      color: "#f5222d",
      details: "Risk of ground instability and flooding. Monitor foundation conditions."
    })
  } else if (values.precipitationIntensity > 2) {
    currentAlerts.push({
      type: "Moderate Precipitation",
      severity: "Medium",
      message: `Moderate precipitation of ${values.precipitationIntensity} mm detected.`,
      icon: CloudRain,
      color: "#faad14",
      details: "Monitor ground conditions and drainage systems."
    })
  }

  // Temperature Alerts
  if (values.temperature > 35) {
    currentAlerts.push({
      type: "High Temperature",
      severity: "Medium",
      message: `High temperature of ${values.temperature}°C may cause thermal expansion.`,
      icon: Thermometer,
      color: "#faad14",
      details: "Monitor line sag and structural expansion. Check equipment cooling systems."
    })
  } else if (values.temperature < 0) {
    currentAlerts.push({
      type: "Low Temperature",
      severity: "Medium",
      message: `Low temperature of ${values.temperature}°C may cause contraction and ice formation.`,
      icon: Thermometer,
      color: "#faad14",
      details: "Monitor for ice formation and structural contraction. Check de-icing systems."
    })
  }

  // Process forecast alerts
  const forecastAlerts = []

  // Check for high wind in forecast
  const highWindForecast = forecastData.find((item) => item.values.windSpeed > 50)
  if (highWindForecast) {
    const forecastTime = new Date(highWindForecast.time).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    })
    forecastAlerts.push({
      type: "Forecasted High Wind",
      severity: "High",
      message: `Wind speed of ${highWindForecast.values.windSpeed} km/h expected at ${forecastTime}.`,
      icon: Wind,
      color: "#f5222d",
      time: forecastTime,
      details: "Prepare for high wind conditions. Review emergency protocols."
    })
  }

  // Check for lightning in forecast
  const lightningForecast = forecastData.find((item) => item.values.lightningStrikeCount > 0)
  if (lightningForecast) {
    const forecastTime = new Date(lightningForecast.time).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    })
    forecastAlerts.push({
      type: "Forecasted Lightning",
      severity: "High",
      message: `Lightning activity expected at ${forecastTime}.`,
      icon: Zap,
      color: "#f5222d",
      time: forecastTime,
      details: "Prepare lightning protection systems. Review safety protocols."
    })
  }

  // Check for heavy precipitation in forecast
  const heavyPrecipForecast = forecastData.find((item) => item.values.precipitationIntensity > 5)
  if (heavyPrecipForecast) {
    const forecastTime = new Date(heavyPrecipForecast.time).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    })
    forecastAlerts.push({
      type: "Forecasted Heavy Precipitation",
      severity: "High",
      message: `Heavy precipitation of ${heavyPrecipForecast.values.precipitationIntensity} mm expected at ${forecastTime}.`,
      icon: CloudRain,
      color: "#f5222d",
      time: forecastTime,
      details: "Prepare for potential flooding. Check drainage systems."
    })
  }

  /**
   * Handle alert acknowledgment
   * In a real application, this would:
   * 1. Log the acknowledgment with timestamp and operator ID
   * 2. Update alert status in the database
   * 3. Trigger any necessary notifications
   * 4. Update the UI to show acknowledged status
   */
  const handleAcknowledge = (alertType: 'current' | 'forecast') => {
    // In a real application, this would make an API call to log the acknowledgment
    console.log(`${alertType} alerts acknowledged`)
    // Example of what would happen in production:
    // - Record timestamp of acknowledgment
    // - Record operator ID who acknowledged
    // - Update alert status in database
    // - Trigger notification to supervisors
    // - Update UI to show acknowledged status
  }

  return (
    <ScrollView style={[styles.container, { backgroundColor: theme.colors.background }]}>
      <View style={styles.header}>
        <Text variant="headlineMedium">Weather Alerts</Text>
      </View>

      {currentAlerts.length === 0 && forecastAlerts.length === 0 ? (
        <Card style={styles.noAlertsCard}>
          <Card.Content style={styles.noAlertsContent}>
            <Text variant="titleMedium">No active weather alerts</Text>
            <Text variant="bodyMedium" style={{ marginTop: 8 }}>
              All weather conditions are currently within safe operating parameters.
            </Text>
          </Card.Content>
        </Card>
      ) : (
        <>
          {currentAlerts.length > 0 && (
            <Card style={styles.alertsCard}>
              <Card.Title
                title="Current Alerts"
                subtitle="Immediate attention required for high-risk conditions"
                left={(props) => <AlertTriangle {...props} color="#f5222d" />}
              />
              <Card.Content>
                <List.Section>
                  {currentAlerts.map((alert, index) => (
                    <React.Fragment key={index}>
                      <List.Item
                        title={alert.type}
                        description={
                          <View>
                            <Text>{alert.message}</Text>
                            <Text style={{ color: theme.colors.primary, marginTop: 4 }}>
                              {alert.details}
                            </Text>
                          </View>
                        }
                        left={(props) => <alert.icon {...props} color={alert.color} />}
                        right={(props) => (
                          <View style={[styles.severityBadge, { backgroundColor: alert.color }]}>
                            <Text style={styles.severityText}>{alert.severity}</Text>
                          </View>
                        )}
                      />
                      {index < currentAlerts.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List.Section>
              </Card.Content>
              <Card.Actions>
                <Button
                  mode="contained"
                  style={{ backgroundColor: "#f5222d" }}
                  onPress={() => handleAcknowledge('current')}
                >
                  Acknowledge Current Alerts
                </Button>
              </Card.Actions>
            </Card>
          )}

          {forecastAlerts.length > 0 && (
            <Card style={styles.alertsCard}>
              <Card.Title
                title="Forecast Alerts"
                subtitle="Upcoming conditions requiring preparation"
                left={(props) => <AlertTriangle {...props} color="#faad14" />}
              />
              <Card.Content>
                <List.Section>
                  {forecastAlerts.map((alert, index) => (
                    <React.Fragment key={index}>
                      <List.Item
                        title={`${alert.type} at ${alert.time}`}
                        description={
                          <View>
                            <Text>{alert.message}</Text>
                            <Text style={{ color: theme.colors.primary, marginTop: 4 }}>
                              {alert.details}
                            </Text>
                          </View>
                        }
                        left={(props) => <alert.icon {...props} color={alert.color} />}
                        right={(props) => (
                          <View style={[styles.severityBadge, { backgroundColor: alert.color }]}>
                            <Text style={styles.severityText}>{alert.severity}</Text>
                          </View>
                        )}
                      />
                      {index < forecastAlerts.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List.Section>
              </Card.Content>
              <Card.Actions>
                <Button
                  mode="contained"
                  style={{ backgroundColor: "#faad14" }}
                  onPress={() => handleAcknowledge('forecast')}
                >
                  Acknowledge Forecast Alerts
                </Button>
              </Card.Actions>
            </Card>
          )}
        </>
      )}

      <Card style={styles.thresholdsCard}>
        <Card.Title
          title="Alert Thresholds"
          subtitle="Reference values for weather condition classifications"
        />
        <Card.Content>
          <View style={styles.thresholdItem}>
            <Text style={styles.thresholdLabel}>High Wind:</Text>
            <Text style={styles.thresholdValue}>{">"} 50 km/h</Text>
            <Text style={styles.thresholdRisk}>(High Risk)</Text>
          </View>
          <Divider style={styles.divider} />
          <View style={styles.thresholdItem}>
            <Text style={styles.thresholdLabel}>Moderate Wind:</Text>
            <Text style={styles.thresholdValue}>{">"} 30 km/h</Text>
            <Text style={styles.thresholdRisk}>(Medium Risk)</Text>
          </View>
          <Divider style={styles.divider} />
          <View style={styles.thresholdItem}>
            <Text style={styles.thresholdLabel}>Lightning:</Text>
            <Text style={styles.thresholdValue}>Any detected</Text>
            <Text style={styles.thresholdRisk}>(High Risk)</Text>
          </View>
          <Divider style={styles.divider} />
          <View style={styles.thresholdItem}>
            <Text style={styles.thresholdLabel}>Heavy Precipitation:</Text>
            <Text style={styles.thresholdValue}>{">"} 5 mm</Text>
            <Text style={styles.thresholdRisk}>(High Risk)</Text>
          </View>
          <Divider style={styles.divider} />
          <View style={styles.thresholdItem}>
            <Text style={styles.thresholdLabel}>Moderate Precipitation:</Text>
            <Text style={styles.thresholdValue}>{">"} 2 mm</Text>
            <Text style={styles.thresholdRisk}>(Medium Risk)</Text>
          </View>
          <Divider style={styles.divider} />
          <View style={styles.thresholdItem}>
            <Text style={styles.thresholdLabel}>High Temperature:</Text>
            <Text style={styles.thresholdValue}>{">"} 35°C</Text>
            <Text style={styles.thresholdRisk}>(Medium Risk)</Text>
          </View>
          <Divider style={styles.divider} />
          <View style={styles.thresholdItem}>
            <Text style={styles.thresholdLabel}>Low Temperature:</Text>
            <Text style={styles.thresholdValue}>{"<"} 0°C</Text>
            <Text style={styles.thresholdRisk}>(Medium Risk)</Text>
          </View>
        </Card.Content>
      </Card>
    </ScrollView>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    marginBottom: 16,
  },
  alertsCard: {
    marginBottom: 16,
  },
  noAlertsCard: {
    marginBottom: 16,
    backgroundColor: "#f6ffed",
    borderColor: "#b7eb8f",
    borderWidth: 1,
  },
  noAlertsContent: {
    alignItems: "center",
    padding: 16,
  },
  severityBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
    alignSelf: "center",
  },
  severityText: {
    color: "white",
    fontWeight: "bold",
  },
  thresholdsCard: {
    marginBottom: 16,
  },
  thresholdItem: {
    flexDirection: "row",
    paddingVertical: 8,
    alignItems: "center",
  },
  thresholdLabel: {
    width: "40%",
    fontWeight: "bold",
  },
  thresholdValue: {
    width: "30%",
  },
  thresholdRisk: {
    width: "30%",
    color: "#8c8c8c",
    fontSize: 12,
  },
  divider: {
    marginVertical: 4,
  },
})
